import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, Users, Loader2, Rocket, MapPin, Plus, X, Tags } from "lucide-react";
import { toast } from "sonner";

const STED = [
  { k: 1, label: "Zeer sterk stedelijk" },
  { k: 2, label: "Sterk stedelijk" },
  { k: 3, label: "Matig stedelijk" },
  { k: 4, label: "Weinig stedelijk" },
  { k: 5, label: "Niet stedelijk" },
];

// Artikelen beheren in één pop-up: producten aanvinken, daarna aantallen/campagneprijzen aanpassen (zelfde methode als bij inkoop/verkoop).
function CampagneArtikelDialog({ regels, onSave, trigger }) {
  const [open, setOpen] = useState(false);
  const [producten, setProducten] = useState([]);
  const [zoek, setZoek] = useState("");
  const [sel, setSel] = useState({}); // product_id -> regel
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/producten").then((r) => setProducten(r.data)).catch((e) => console.warn("Producten laden mislukt:", e));
    const init = {};
    (regels || []).forEach((r) => {
      const key = r.product_id || r.sku;
      init[key] = {
        product_id: r.product_id, sku: r.sku, naam: r.naam,
        aantal: r.aantal, prijs: r.prijs, standaardprijs: r.standaardprijs ?? r.prijs,
      };
    });
    setSel(init); setZoek("");
    /* eslint-disable-next-line */
  }, [open]);

  const toggle = (p) => setSel((prev) => {
    const n = { ...prev };
    if (n[p.id]) { delete n[p.id]; return n; }
    const vp = Number(p.verkoopprijs || 0);
    n[p.id] = { product_id: p.id, sku: p.sku, naam: p.naam, aantal: 1, prijs: vp, standaardprijs: vp };
    return n;
  });
  const upd = (key, veld, val) => setSel((prev) => ({ ...prev, [key]: { ...prev[key], [veld]: val } }));

  const gefilterd = useMemo(() => {
    const t = zoek.trim().toLowerCase();
    return producten.filter((p) => !t
      || (p.naam || "").toLowerCase().includes(t)
      || (p.sku || "").toLowerCase().includes(t)).slice(0, 60);
  }, [zoek, producten]);

  const num = (v) => parseFloat(String(v).replace(",", ".")) || 0;
  const gekozen = Object.values(sel);
  const totaal = gekozen.reduce((s, r) => s + num(r.aantal) * num(r.prijs), 0);

  const opslaan = async () => {
    setBusy(true);
    try {
      const next = gekozen.map((r) => ({
        product_id: r.product_id, sku: r.sku, naam: r.naam,
        aantal: num(r.aantal) || 1, prijs: num(r.prijs), standaardprijs: Number(r.standaardprijs) || 0,
      }));
      onSave(next);
      setOpen(false);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-5xl w-[95vw]" data-testid="campagne-artikelen-dialog">
        <DialogHeader><DialogTitle>Artikelen beheren</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Links: aanvinkbare productenlijst */}
          <div>
            <Input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Zoek product op naam of SKU…"
              data-testid="campagne-artikel-zoek" autoComplete="off" />
            <div className="mt-2 border border-border rounded-lg max-h-[52vh] overflow-y-auto divide-y divide-border">
              {gefilterd.map((p) => (
                <label key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-secondary cursor-pointer text-sm"
                  data-testid={`campagne-artikel-optie-${p.sku}`}>
                  <Checkbox checked={!!sel[p.id]} onCheckedChange={() => toggle(p)} />
                  <span className="font-mono text-xs text-muted-foreground w-20 shrink-0">{p.sku}</span>
                  <span className="flex-1 leading-snug">{p.naam}</span>
                  <span className="text-xs shrink-0 text-right text-muted-foreground">€ {(Number(p.verkoopprijs) || 0).toFixed(2)}</span>
                </label>
              ))}
              {gefilterd.length === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground">Geen artikelen gevonden</div>}
            </div>
          </div>
          {/* Rechts: gekozen met aantal + campagneprijs */}
          <div>
            <div className="text-xs text-muted-foreground mb-2">{gekozen.length} geselecteerd</div>
            <div className="space-y-2 max-h-[52vh] overflow-y-auto pr-1">
              {gekozen.length === 0 && <div className="text-sm text-muted-foreground italic">Vink links artikelen aan.</div>}
              {gekozen.map((r) => {
                const key = r.product_id || r.sku;
                return (
                <div key={key} className="border border-border rounded-lg p-2.5 space-y-1.5" data-testid={`campagne-artikel-gekozen-${r.sku}`}>
                  <div className="text-sm font-medium leading-snug">{r.naam}</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Label className="text-[10px] text-muted-foreground">Aantal</Label>
                      <Input type="number" min="0" step="1" value={r.aantal} className="h-8"
                        onChange={(e) => upd(key, "aantal", e.target.value)} data-testid={`campagne-artikel-aantal-${r.sku}`} />
                    </div>
                    <div className="flex-1">
                      <Label className="text-[10px] text-muted-foreground">Campagneprijs (€)</Label>
                      <Input type="number" min="0" step="0.01" value={r.prijs} className="h-8 text-right"
                        onChange={(e) => upd(key, "prijs", e.target.value)} data-testid={`campagne-artikel-prijs-${r.sku}`} />
                    </div>
                    <div className="text-right w-20">
                      <Label className="text-[10px] text-muted-foreground">Regel</Label>
                      <div className="h-8 flex items-center justify-end text-sm tabular-nums">€ {(num(r.aantal) * num(r.prijs)).toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              );})}
            </div>
          </div>
        </div>
        <DialogFooter className="items-center">
          <div className="mr-auto text-sm font-medium" data-testid="campagne-artikelen-totaal">Totaal: € {totaal.toFixed(2)}</div>
          <Button variant="outline" onClick={() => setOpen(false)}>Annuleren</Button>
          <Button onClick={opslaan} disabled={busy} data-testid="campagne-artikelen-opslaan">{busy ? "Bezig…" : "Opslaan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CampagneAI() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const [merk, setMerk] = useState("alle");
  const [productZoek, setProductZoek] = useState("");
  const [productgroep, setProductgroep] = useState("");
  const [plaats, setPlaats] = useState("");
  const [steden, setSteden] = useState(() => new Set());
  const [alleenBesteld, setAlleenBesteld] = useState(true);

  // kenmerken
  const [defs, setDefs] = useState([]);
  const [kenmerken, setKenmerken] = useState(() => new Set());
  const [kenmerkenModus, setKenmerkenModus] = useState("any");

  useEffect(() => {
    api.get("/kenmerk-definities").then(({ data }) => setDefs(data || [])).catch(() => {});
  }, []);

  const [naam, setNaam] = useState("");
  const [planInschakelen, setPlanInschakelen] = useState(false);
  const [planStartDatum, setPlanStartDatum] = useState("");
  const [planStartTijd, setPlanStartTijd] = useState("09:00");
  const [omschrijving, setOmschrijving] = useState("");
  const [emailTekst, setEmailTekst] = useState("");
  const [briefTemplates, setBriefTemplates] = useState([]);
  const [briefTemplate, setBriefTemplate] = useState("_standaard");

  useEffect(() => {
    api.get("/templates").then(({ data }) => {
      setBriefTemplates((data.designs || []).filter((d) => d.type === "samplebrief"));
    }).catch(() => {});
  }, []);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);

  // optionele producten voor het campagne-aanbod
  const [gekozen, setGekozen] = useState([]); // {product_id, sku, naam, aantal, prijs, standaardprijs}

  // selecteerbaar "besteld product"-filter
  const [prodFilterResults, setProdFilterResults] = useState([]);
  const zoekFilterProduct = async (v) => {
    setProductZoek(v);
    if (!v.trim()) { setProdFilterResults([]); return; }
    try { const { data } = await api.get("/producten", { params: { q: v } }); setProdFilterResults(data.slice(0, 8)); }
    catch { setProdFilterResults([]); }
  };
  const kiesFilterProduct = (p) => { setProductZoek(p.naam); setProdFilterResults([]); };

  const filters = () => ({
    merk: merk === "alle" ? null : merk,
    product_zoek: productZoek || null,
    productgroep: productgroep || null,
    stedelijkheid: steden.size ? [...steden] : null,
    plaats: plaats || null,
    kenmerken: kenmerken.size ? [...kenmerken] : null,
    kenmerken_modus: kenmerkenModus,
    alleen_besteld: alleenBesteld,
  });

  const toggleSted = (k) => setSteden((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const toggleKenmerk = (w) => setKenmerken((s) => { const n = new Set(s); n.has(w) ? n.delete(w) : n.add(w); return n; });

  const analyseer = async () => {
    if (!prompt.trim()) return;
    setAiLoading(true);
    try {
      const { data } = await api.post("/campagne-ai/parse", { prompt });
      setMerk(data.merk || "alle");
      setProductZoek(data.product_zoek || "");
      setProductgroep(data.productgroep || "");
      setPlaats(data.plaats || "");
      setSteden(new Set(Array.isArray(data.stedelijkheid) ? data.stedelijkheid : []));
      setKenmerken(new Set(Array.isArray(data.kenmerken) ? data.kenmerken : []));
      if (["any", "all", "like"].includes(data.kenmerken_modus)) setKenmerkenModus(data.kenmerken_modus);
      setAlleenBesteld(data.alleen_besteld !== false);
      if (data.naam) setNaam(data.naam);
      if (data.omschrijving) setOmschrijving(data.omschrijving);
      if (data.email_tekst) setEmailTekst(data.email_tekst);
      toast.success("AI heeft de filters ingevuld");
    } catch (e) {
      toast.error("AI-analyse mislukt: " + (e?.response?.data?.detail || e.message));
    } finally { setAiLoading(false); }
  };

  const toonDoelgroep = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.post("/campagne-ai/preview", filters());
      setPreview(data);
      if (data.totaal === 0) toast.info("Geen organisaties gevonden met deze filters");
    } catch { toast.error("Doelgroep ophalen mislukt"); }
    finally { setPreviewLoading(false); }
  };

  const updateGekozen = (pid, veld, val) => setGekozen((g) =>
    g.map((x) => x.product_id === pid ? { ...x, [veld]: val } : x));
  const verwijderGekozen = (pid) => setGekozen((g) => g.filter((x) => x.product_id !== pid));

  const genereer = async () => {
    if (!naam.trim()) return toast.error("Geef de campagne een naam");
    if (!preview || preview.totaal === 0) return toast.error("Toon eerst een doelgroep met minimaal 1 organisatie");
    setGenLoading(true);
    try {
      const relatie_ids = preview.alle_ids || preview.organisaties.map((o) => o.id);
      const product_lijst = gekozen.map((g) => ({
        product_id: g.product_id,
        aantal: Number(g.aantal) || 1,
        prijs: g.prijs === "" || g.prijs == null ? null : Number(g.prijs),
      }));
      const { data } = await api.post("/campagne-ai/genereer", {
        naam, omschrijving, product_lijst, relatie_ids,
        samplebrief_template_id: briefTemplate === "_standaard" ? null : briefTemplate,
        planning: (planInschakelen && planStartDatum)
          ? { start_datum: planStartDatum, start_tijd: planStartTijd || "09:00" }
          : null,
      });
      const ingepland = data.ingepland ? ` · ${data.ingepland} ingepland` : "";
      toast.success(`Campagne aangemaakt — ${data.aangemaakt} verkoopkans(en) gegenereerd${ingepland}`);
      navigate(`/campagnes/${data.campagne_id}`);
    } catch (e) {
      toast.error("Genereren mislukt: " + (e?.response?.data?.detail || e.message));
    } finally { setGenLoading(false); }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="campagne-ai-page">
      <div>
        <div className="text-sm text-muted-foreground flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-accent" /> Campagnes</div>
        <h1 className="font-display text-4xl font-medium tracking-tight mt-1">AI-campagnetool</h1>
        <p className="text-sm text-muted-foreground mt-1">Bouw doelgroepen op orderhistorie en stedelijkheid (CBS), en genereer direct een campagne met verkoopkansen.</p>
      </div>

      {/* AI-briefing */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-3">
        <Label className="text-sm font-medium">Beschrijf je campagne in gewone taal</Label>
        <Textarea data-testid="ai-prompt" rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)}
          placeholder="Bijv. 'Klanten die ooit een NextBrush-display bestelden en in een sterk stedelijk gebied zitten'" />
        <Button onClick={analyseer} disabled={aiLoading} data-testid="ai-analyseer-btn">
          {aiLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
          AI analyseren
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="font-medium">Doelgroepfilters</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-muted-foreground">Merk</Label>
            <Select value={merk} onValueChange={setMerk}>
              <SelectTrigger data-testid="filter-merk"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle merken</SelectItem>
                <SelectItem value="NextBrush">NextBrush</SelectItem>
                <SelectItem value="Frankly">Frankly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Besteld product</Label>
            <div className="relative">
              <Input data-testid="filter-product" value={productZoek}
                onChange={(e) => zoekFilterProduct(e.target.value)}
                placeholder="Zoek en selecteer een product…" autoComplete="off" />
              {productZoek && (
                <button type="button" onClick={() => { setProductZoek(""); setProdFilterResults([]); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="filter-product-clear"><X className="h-4 w-4" /></button>
              )}
              {prodFilterResults.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-card border border-border rounded-lg shadow-lg max-h-56 overflow-y-auto">
                  {prodFilterResults.map((p) => (
                    <button key={p.id} type="button" onClick={() => kiesFilterProduct(p)}
                      data-testid={`filter-product-optie-${p.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-secondary text-sm flex items-center gap-2">
                      <span className="font-mono text-xs text-muted-foreground">{p.sku}</span> {p.naam}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Productgroep (optioneel)</Label>
            <Input data-testid="filter-productgroep" value={productgroep} onChange={(e) => setProductgroep(e.target.value)} placeholder="bijv. display" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Plaats (optioneel)</Label>
            <Input data-testid="filter-plaats" value={plaats} onChange={(e) => setPlaats(e.target.value)} placeholder="bijv. Amsterdam" />
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Stedelijkheid (CBS)</Label>
          <div className="flex flex-wrap gap-3 mt-2">
            {STED.map((s) => (
              <label key={s.k} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`filter-sted-${s.k}`}>
                <Checkbox checked={steden.has(s.k)} onCheckedChange={() => toggleSted(s.k)} />
                {s.k}. {s.label}
              </label>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={alleenBesteld} onCheckedChange={(v) => setAlleenBesteld(!!v)} data-testid="filter-alleen-besteld" />
          Alleen organisaties die het gekozen product/merk ooit hebben besteld
        </label>
        {/* Kenmerken-filter */}
        {defs.length > 0 && (
          <div data-testid="kenmerken-filter" className="border-t border-border pt-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><Tags className="h-3.5 w-3.5" /> Kenmerken</Label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Match:</span>
                <Select value={kenmerkenModus} onValueChange={setKenmerkenModus}>
                  <SelectTrigger className="h-8 w-[220px]" data-testid="kenmerken-modus"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Minimaal één (ANY)</SelectItem>
                    <SelectItem value="all">Alle (ALL)</SelectItem>
                    <SelectItem value="like">Gedeeltelijk (LIKE)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
              {defs.map((d) => (
                (d.waarden || []).length > 0 ? (
                  <div key={d.id || d.categorie}>
                    <div className="text-xs font-medium text-foreground mb-1.5">{d.categorie}</div>
                    <div className="flex flex-wrap gap-2">
                      {d.waarden.map((w) => {
                        const actief = kenmerken.has(w);
                        return (
                          <button
                            key={w}
                            type="button"
                            onClick={() => toggleKenmerk(w)}
                            data-testid={`kenmerk-${w}`}
                            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${actief ? "bg-accent text-accent-foreground border-accent" : "bg-secondary/40 border-border hover:bg-secondary"}`}
                          >
                            {w}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null
              ))}
            </div>
            {kenmerken.size > 0 && (
              <div className="text-xs text-muted-foreground" data-testid="kenmerken-selectie">
                {kenmerken.size} kenmerk(en) geselecteerd
              </div>
            )}
          </div>
        )}

        <Button variant="outline" onClick={toonDoelgroep} disabled={previewLoading} data-testid="toon-doelgroep-btn">
          {previewLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Users className="h-4 w-4 mr-2" />}
          Doelgroep tonen
        </Button>
      </div>

      {/* Doelgroep-resultaat */}
      {preview && (
        <div className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid="doelgroep-resultaat">
          <div className="flex items-center justify-between">
            <div className="font-medium">Doelgroep: <span className="text-accent" data-testid="doelgroep-totaal">{preview.totaal}</span> organisaties</div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(preview.verdeling || {}).map(([lbl, n]) => (
                <Badge key={lbl} variant="outline" className="text-xs">{lbl}: {n}</Badge>
              ))}
            </div>
          </div>
          {preview.totaal > 0 && (
            <div className="max-h-64 overflow-y-auto border border-border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-xs uppercase sticky top-0">
                  <tr><th className="text-left p-2">Organisatie</th><th className="text-left p-2">Plaats</th><th className="text-left p-2">Stedelijkheid</th><th className="text-left p-2">Ooit besteld</th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {preview.organisaties.map((o) => (
                    <tr key={o.id}>
                      <td className="p-2 font-medium">{o.naam}</td>
                      <td className="p-2 text-muted-foreground">{o.plaats}</td>
                      <td className="p-2 text-muted-foreground">{o.stedelijkheid_label}</td>
                      <td className="p-2">{o.ooit_besteld ? <Badge variant="outline" className="text-[10px]">ja</Badge> : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Campagne aanmaken */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="font-medium">Campagne</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-muted-foreground">Campagnenaam</Label>
            <Input data-testid="campagne-naam" value={naam} onChange={(e) => setNaam(e.target.value)} placeholder="Campagnenaam" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Omschrijving</Label>
            <Input data-testid="campagne-omschrijving" value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)} placeholder="Korte omschrijving" />
          </div>
        </div>

        {/* Verkoopkansen inplannen (belslots van 3 min) */}
        <div className="rounded-lg border border-border p-3 space-y-3">
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <Checkbox checked={planInschakelen} onCheckedChange={(v) => setPlanInschakelen(!!v)} data-testid="plan-inschakelen" />
            Verkoopkansen inplannen (belslots van 3 min)
          </label>
          {planInschakelen && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Startdatum</Label>
                <Input type="date" value={planStartDatum} onChange={(e) => setPlanStartDatum(e.target.value)} data-testid="plan-startdatum" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Starttijd</Label>
                <Input type="time" value={planStartTijd} onChange={(e) => setPlanStartTijd(e.target.value)} data-testid="plan-starttijd" />
              </div>
              <p className="sm:col-span-2 text-xs text-muted-foreground">
                Belletjes worden om de 3 minuten ingepland op werkdagen tussen 09:00 en 17:30 (lunch 12:00–13:00 overgeslagen) en rollen automatisch door naar de volgende werkdag.
              </p>
            </div>
          )}
        </div>
        {emailTekst && (
          <div>
            <Label className="text-xs text-muted-foreground">Voorgestelde e-mailtekst (AI)</Label>
            <Textarea rows={5} value={emailTekst} onChange={(e) => setEmailTekst(e.target.value)} data-testid="campagne-email" />
          </div>
        )}
        {/* Optioneel: producten + campagneprijzen voor de verkoopkansen */}
        <div>
          <Label className="text-xs text-muted-foreground">Producten voor de verkoopkansen (optioneel)</Label>
          <p className="text-[11px] text-muted-foreground mb-2">
            Voeg artikelen toe via hetzelfde venster als bij verkoopkansen en stel de campagneprijs in. Klantspecifieke prijsafspraken hebben altijd voorrang per organisatie.
          </p>
          <CampagneArtikelDialog regels={gekozen} onSave={setGekozen}
            trigger={<Button type="button" variant="outline" data-testid="campagne-artikel-toevoegen-btn"><Plus className="h-4 w-4 mr-2" /> Artikelen beheren</Button>} />
          {gekozen.length > 0 && (
            <div className="mt-3 border border-border rounded-lg overflow-hidden" data-testid="campagne-producten-tabel">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left p-2">Product</th>
                    <th className="text-right p-2 w-24">Aantal</th>
                    <th className="text-right p-2 w-32">Campagneprijs</th>
                    <th className="text-right p-2 w-28">Regel</th>
                    <th className="p-2 w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {gekozen.map((g) => {
                    const regel = (Number(g.aantal) || 0) * (Number(g.prijs) || 0);
                    const gewijzigd = Number(g.prijs) !== Number(g.standaardprijs);
                    return (
                      <tr key={g.product_id} data-testid={`campagne-product-rij-${g.product_id}`}>
                        <td className="p-2">
                          <div className="font-medium leading-tight">{g.naam}</div>
                          <div className="font-mono text-[11px] text-muted-foreground">{g.sku}</div>
                        </td>
                        <td className="p-2">
                          <Input type="number" min="1" step="1" value={g.aantal}
                            onChange={(e) => updateGekozen(g.product_id, "aantal", e.target.value)}
                            className="h-8 text-right" data-testid={`campagne-product-aantal-${g.product_id}`} />
                        </td>
                        <td className="p-2">
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€</span>
                            <Input type="number" min="0" step="0.01" value={g.prijs}
                              onChange={(e) => updateGekozen(g.product_id, "prijs", e.target.value)}
                              className="h-8 text-right pl-5" data-testid={`campagne-product-prijs-${g.product_id}`} />
                          </div>
                          {gewijzigd && (
                            <button type="button" onClick={() => updateGekozen(g.product_id, "prijs", g.standaardprijs)}
                              className="text-[10px] text-accent hover:underline mt-0.5"
                              data-testid={`campagne-product-reset-${g.product_id}`}>
                              herstel € {Number(g.standaardprijs).toFixed(2)}
                            </button>
                          )}
                        </td>
                        <td className="p-2 text-right tabular-nums">€ {regel.toFixed(2)}</td>
                        <td className="p-2 text-right">
                          <button type="button" onClick={() => verwijderGekozen(g.product_id)}
                            className="text-muted-foreground hover:text-destructive"
                            data-testid={`campagne-product-verwijder-${g.product_id}`}><X className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-secondary/30 text-sm font-medium">
                  <tr>
                    <td className="p-2" colSpan={3}>Totaal per verkoopkans</td>
                    <td className="p-2 text-right tabular-nums" data-testid="campagne-producten-totaal">
                      € {gekozen.reduce((s, g) => s + (Number(g.aantal) || 0) * (Number(g.prijs) || 0), 0).toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
        {/* Samplebrief-template voor deze campagne */}
        <div>
          <Label className="text-xs text-muted-foreground">Samplebrief-template voor deze campagne</Label>
          <p className="text-[11px] text-muted-foreground mb-2">
            Samplebrieven van verkoopkansen uit deze campagne gebruiken automatisch dit template (beheer templates via Templates → Template-bibliotheek).
          </p>
          <Select value={briefTemplate} onValueChange={setBriefTemplate}>
            <SelectTrigger className="w-full sm:w-80" data-testid="campagne-samplebrief-template">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_standaard">Standaard / gekoppeld template</SelectItem>
              {briefTemplates.map((d) => <SelectItem key={d.id} value={d.id}>{d.naam}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={genereer} disabled={genLoading} data-testid="genereer-btn" className="w-full sm:w-auto">
          {genLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Rocket className="h-4 w-4 mr-2" />}
          Campagne aanmaken + verkoopkansen genereren
        </Button>
      </div>
    </div>
  );
}
